import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import 'react-dropdown/style.css';
import { FaArrowRight, FaChartPie, FaPencilAlt, FaSearch, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import 'reactjs-popup/dist/index.css';
import { BrcContext } from '../BrcContextWrapper';
import CampaignForm from '../components/CampaignForm';
import ConfigAccordion from '../components/ConfigAccordion';
import DataTable from '../components/datatable';
import DetailModal from '../components/DetailModal';
import ExportForm from '../components/ExportForm';
import PopUpModal from '../components/PopUpModal';
import brandMetricTypeData from '../config/dropdowns/brandMetricType.json';
import countryData from '../config/dropdowns/country.json';
import industryData from '../config/dropdowns/industry.json';
import {
  CAMPAIGN_DEMAND_SOURCES_LIST,
  CAMPAIGN_DETAILED_STATUSES,
  EMPTY_CAMPAIGN_FILTERS,
} from '../constants/campaign';
import { deleteCampaign, getAllCampaigns, getAllQuestions } from '../service/brc';
import { getDemandSourceLabel } from '../utils/demandSource';

export default function Campaign() {
  const {
      show,
      toggleShow,
      deleteShow,
      toggleDelete,
      isEdit,
      toggleIsEdit,
      toggleDetails,
      detailsShow,
      exportShow,
      toggleExport,
      sessionUser,
      campaignColumns,
    } = useContext(BrcContext),
    [selectedRow, setSelectedRow] = useState('');

  const [campaigns, setCampaigns] = useState([]);

  const [questions, setQuestions] = useState([]);

  const [pending, setPending] = useState(true);

  const [exportData, setExportData] = useState([]);

  const exportRef = useRef();

  const navigate = useNavigate();

  const [currentCampaignToDelete, setCurrentCampaignToDelete] = useState();

  const handleClose = () => toggleDelete();
  const closeDetails = () => toggleDetails();
  const handleShow = (e) => {
    toggleShow();
  };

  const [data, setData] = useState([]);

  const exportDataAll = campaigns.map((item) => ({
    ID: item._id,
    OpportunityID: item.opportunityId,
    Name: item.campaignName,
    StartDate: moment(item.campaignStart).format('ll'),
    EndDate: moment(item.campaignEnd).format('ll'),
    FirstImpressionDate: item.firstImpressionDate
      ? moment(item.firstImpressionDate).format('ll')
      : '-',
    GeneralCount: item.viewCount + '/' + item.submitCount,
    ExposedCount: item.exposedSubmit ? item.exposedSubmit + '/' + item.exposedView : '-',
    ControlCount: item.controlSubmit ? item.controlSubmit + '/' + item.controlView : '-',
    Details: item.campaignDetails,
  }));
  const headers = [
    { label: 'ID', key: 'ID' },
    { label: 'Opportunity ID', key: 'OpportunityID' },
    { label: 'Name', key: 'Name' },
    { label: 'Start Date', key: 'StartDate' },
    { label: 'End Date', key: 'EndDate' },
    { label: 'First Impression Date', key: 'FirstImpressionDate' },
    { label: 'Responses/Views(General)', key: 'GeneralCount' },
    { label: 'Responses/Views(Exposed)', key: 'ExposedCount' },
    { label: 'Responses/Views(Control)', key: 'ControlCount' },
    { label: 'Details', key: 'Details' },
  ];

  const applyFilters = (dataset, filters) => {
    const filteredData = dataset.filter((item) => {
      // Name filter
      if (
        filters.name !== '' &&
        !item.campaignName?.toLowerCase()?.includes(filters.name.toLowerCase())
      ) {
        return false;
      }
      // Campaign ID filter
      if (
        filters.campaignId !== '' &&
        !item.id?.toLowerCase().includes(filters.campaignId.toLowerCase())
      ) {
        return false;
      }
      // Campaign Demand Source filter
      if (
        filters.campaignDemandSource !== '' &&
        item.campaignDemandSource !== filters.campaignDemandSource
      ) {
        return false;
      }
      // Detailed status filter
      if (filters.detailedStatus !== '' && item.detailedStatus !== filters.detailedStatus) {
        return false;
      }
      // Country filter
      if (filters.country !== '' && item.country !== filters.country) {
        return false;
      }
      // Industry filter
      if (filters.industry !== '' && item.industry !== filters.industry) {
        return false;
      }
      // Start date filter: include items with a start date greater than or equal to the selected start date
      if (filters.startDate !== '' && new Date(item.campaignStart) < new Date(filters.startDate)) {
        return false;
      }
      // End date filter: include items with an end date less than or equal to the selected end date
      if (filters.endDate !== '' && new Date(item.campaignEnd) > new Date(filters.endDate)) {
        return false;
      }
      // Ensure end date is not lower than the start date (if both are selected)
      if (
        filters.startDate !== '' &&
        filters.endDate &&
        new Date(filters.endDate) < new Date(filters.startDate)
      ) {
        return false;
      }
      // Brand metric type filter
      if (filters.brandMetricType !== '') {
        return questions.some(
          (question) =>
            question.campaignId === item.id && question.brandMetricType === filters.brandMetricType
        );
      }
      return true;
    });

    return filteredData;
  };

  const [filteredItems, setFilteredItems] = useState();
  const [filterData, setFilterData] = useState({ ...EMPTY_CAMPAIGN_FILTERS });

  useEffect(() => {
    const filtered = applyFilters(data, filterData);
    if (filtered) {
      setFilteredItems(filtered);
    } else {
      setFilteredItems(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, data]);

  //Data Table Style
  const customStyles = {
    rows: {
      style: {
        minHeight: '40px',
      },
    },
    headCells: {
      style: {
        backgroundColor: ' #cccccc',
        fontWeight: 'bold',
        fontSize: '13px',
      },
    },
  };

  const shouldOmitColumn = useCallback(
    (columnId) => !campaignColumns.includes(columnId),
    [campaignColumns]
  );

  const columns = [
    {
      name: 'Actions',
      selector: (row) => (
        <div>
          <OverlayTrigger placement="top" overlay={<Tooltip>Details</Tooltip>}>
            <Button variant="outline-info" size="sm" onClick={() => campaignHandler(row)}>
              <FaSearch />
            </Button>
          </OverlayTrigger>{' '}
          <OverlayTrigger placement="top" overlay={<Tooltip>See The Report</Tooltip>}>
            <Button variant="dark" size="sm" onClick={() => detailReport(row)}>
              <FaChartPie />
            </Button>
          </OverlayTrigger>{' '}
          {sessionUser && sessionUser.isAdmin && (
            <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
              <Button variant="primary" size="sm" onClick={() => editHandler(row)}>
                <FaPencilAlt />
              </Button>
            </OverlayTrigger>
          )}{' '}
          {sessionUser && sessionUser.isAdmin && (
            <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
              <Button
                variant="danger"
                size="sm"
                onClick={() => deleteHandler(row)}
                disabled={row.firstImpressionDate}
              >
                <FaTrash />
              </Button>
            </OverlayTrigger>
          )}{' '}
          <OverlayTrigger placement="top" overlay={<Tooltip>See Questions</Tooltip>}>
            <Button variant="info" size="sm" onClick={() => detailHandler(row)}>
              <FaArrowRight />
            </Button>
          </OverlayTrigger>{' '}
        </div>
      ),
      id: 'actions',
      allowOverflow: true,
      center: true,
      showInFilters: false,
      showInColumnFilters: false,
    },
    {
      name: 'Status',
      selector: (row) => row.detailedStatus,
      label: 'Status',
      id: 'detailedStatus',
      showInFilters: true,
      showInColumnFilters: true,
      type: 'select',
      options: Object.values(CAMPAIGN_DETAILED_STATUSES),
    },
    {
      name: 'Campaign ID',
      selector: (row) => row.id,
      label: 'Campaign ID',
      id: 'campaignId',
      showInFilters: true,
      type: 'text',
      showInColumnFilters: true,
    },
    {
      name: 'Demand Source',
      selector: (row) => getDemandSourceLabel(row.campaignDemandSource),
      sortable: true,
      id: 'campaignDemandSource',
      label: 'Demand Source',
      showInFilters: true,
      type: 'select',
      showInColumnFilters: true,
      options: CAMPAIGN_DEMAND_SOURCES_LIST,
    },
    {
      name: 'Name',
      selector: (row) => row.campaignName,
      sortable: true,
      id: 'name',
      label: 'Name',
      showInFilters: true,
      type: 'text',
      showInColumnFilters: false,
    },
    {
      name: 'Country',
      selector: (row) => row.country,
      sortable: true,
      id: 'country',
      label: 'Country',
      showInFilters: true,
      type: 'select',
      showInColumnFilters: true,
      options: countryData,
    },
    {
      name: 'Industry',
      selector: (row) => row.industry,
      sortable: true,
      id: 'industry',
      label: 'Industry',
      showInFilters: true,
      type: 'select',
      showInColumnFilters: true,
      options: industryData,
    },
    {
      name: 'Start Date',
      selector: (row) => (row.campaignStart ? row.campaignStart : null),
      format: (row) => moment(row.campaignStart).format('ll'),
      sortable: true,
      center: true,
      id: 'startDate',
      label: 'Start Date',
      showInFilters: true,
      type: 'date',
      showInColumnFilters: true,
      filterOptions: {
        max: filterData.endDate,
      },
    },
    {
      name: 'End Date',
      selector: (row) => (row.campaignEnd ? row.campaignEnd : null),
      format: (row) => moment(row.campaignEnd).format('ll'),
      sortable: true,
      center: true,
      id: 'endDate',
      label: 'End Date',
      showInFilters: true,
      type: 'date',
      showInColumnFilters: true,
      filterOptions: {
        min: filterData.startDate,
      },
    },
    {
      name: 'First Imp Date',
      selector: (row) => (row.firstImpressionDate ? row.firstImpressionDate : null),
      format: (row) =>
        row.firstImpressionDate ? moment(row.firstImpressionDate).format('ll') : '-',
      sortable: true,
      center: true,
      id: 'firstImpDate',
      label: 'First Imp Date',
      showInFilters: false,
      type: 'date',
      showInColumnFilters: true,
    },
    {
      name: (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <span style={{ width: '100%', paddingBottom: '3px' }}>Responses</span>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ width: '100px', textAlign: 'center', fontSize: '11px' }}>
              Controlled
            </span>
            <span style={{ width: '100px', textAlign: 'center', fontSize: '11px' }}>Exposed</span>
            <span style={{ width: '100px', textAlign: 'center', fontSize: '11px' }}>Total</span>
          </div>
        </div>
      ),
      selector: (row) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', width: '100px', textAlign: 'center' }}>
              {row.controlSubmit}
            </span>
            <span style={{ fontSize: '11px', width: '100px', textAlign: 'center' }}>
              {row.exposedSubmit}
            </span>
            <span style={{ fontSize: '11px', width: '100px', textAlign: 'center' }}>
              {row.submitCount}
            </span>
          </div>
        );
      },
      center: false,
      id: 'votes',
      label: 'Responses',
      showInFilters: false,
      showInColumnFilters: true,
    },
    {
      name: (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <span style={{ width: '100%', paddingBottom: '3px' }}>Views</span>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ width: '100px', textAlign: 'center', fontSize: '11px' }}>
              Controlled
            </span>
            <span style={{ width: '100px', textAlign: 'center', fontSize: '11px' }}>Exposed</span>
            <span style={{ width: '100px', textAlign: 'center', fontSize: '11px' }}>Total</span>
          </div>
        </div>
      ),
      selector: (row) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', width: '100px', textAlign: 'center' }}>
              {' '}
              {row.controlView}
            </span>
            <span style={{ fontSize: '11px', width: '100px', textAlign: 'center' }}>
              {' '}
              {row.exposedView}
            </span>
            <span style={{ fontSize: '11px', width: '100px', textAlign: 'center' }}>
              {' '}
              {row.viewCount}
            </span>
          </div>
        );
      },
      center: false,
      id: 'views',
      label: 'Views',
      showInFilters: false,
      showInColumnFilters: true,
    },
    {
      name: 'Brand Metric Type',
      selector: (row) => row.industry,
      sortable: true,
      id: 'brandMetricType',
      label: 'Brand Metric Type',
      showInFilters: true,
      type: 'select',
      showInColumnFilters: false,
      options: brandMetricTypeData,
    },
  ].map((column) => ({ ...column, omit: shouldOmitColumn(column.id) }));

  const deleteHandler = (state) => {
      setCurrentCampaignToDelete(state.id);
      toggleDelete();
    },
    deleteObject = () => {
      let reqData = {
        email: sessionUser.email,
      };
      deleteCampaign(reqData, currentCampaignToDelete).then(() => {
        toggleDelete();
        window.location.reload();
      });
    },
    editHandler = (state) => {
      setSelectedRow(state);
      toggleIsEdit(true);
      handleShow();
    },
    createHandler = () => {
      setSelectedRow({
        id: '',
        automatic: true,
        opportunityId: '',
        campaignName: '',
        campaignStart: '',
        campaignEnd: '',
        campaignDetails: '',
      });
      toggleIsEdit(false);
      handleShow();
    },
    campaignHandler = (state) => {
      setSelectedRow(state);
      toggleDetails();
    },
    detailHandler = (state) => {
      navigate('/Questions', { state: state });
    },
    detailReport = (state) => {
      navigate('/Reports', { state: state });
    },
    exportHandler = (data) => {
      if (data.campaignStart && data.campaignEnd) {
        setExportData(
          exportDataAll.filter((oldData) => {
            let oldDate = new Date(oldData.StartDate),
              start = new Date(data.campaignStart),
              end = new Date(data.campaignEnd);
            return oldDate >= start && oldDate <= end;
          })
        );
      }
    };

  useEffect(() => {
    if (!sessionUser.email) {
      navigate('/Login');
    } else {
      getAllQuestions().then((res) => {
        setQuestions(res.data._data[0].QuestionList);
        setPending(false);
      });
      getAllCampaigns().then((res) => {
        setCampaigns(res.data._data[0].CampaignList.reverse());
        window.history.replaceState(null, null, window.location.pathname);
      });
    }
  }, [navigate, sessionUser.email]);

  useEffect(() => {
    setData(
      campaigns.map((item) => {
        return {
          id: item._id,
          ...item,
        };
      })
    );
  }, [campaigns]);

  useEffect(() => {
    if (exportData.length > 0) {
      exportRef.current.link.click();
    }
  }, [exportData]);

  return (
    <div>
      {sessionUser && sessionUser.isAdmin && (
        <Button
          style={{
            fontWeight: 'bold',
            margin: '10px',
            float: 'right',
          }}
          variant="primary"
          onClick={() => createHandler()}
        >
          Create Campaign
        </Button>
      )}
      <CSVLink data={exportData} headers={headers} filename={'brc-campaigns.csv'} ref={exportRef} />
      <CampaignForm show={show} setShow={toggleShow} isEdit={isEdit} campaign={selectedRow} />
      <ExportForm show={exportShow} setShow={toggleExport} sendData={exportHandler} />
      <DetailModal detailsShow={detailsShow} closeDetails={closeDetails} campaign={selectedRow} />
      <PopUpModal
        deleteText="campaign"
        deleteShow={deleteShow}
        handleClose={handleClose}
        deleteObject={deleteObject}
      />
      <h2 style={{ marginTop: '15px' }}>Campaigns</h2>
      <div style={{ padding: '15px' }}>
        <ConfigAccordion columns={columns} filterData={filterData} setFilterData={setFilterData} />
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          customStyles={customStyles}
          subHeader
          persistTableHead
          progressPending={pending}
        />
      </div>
    </div>
  );
}
